import { FC, Fragment } from "react";
import { observer } from "mobx-react-lite";
import { SnippetChunk, TopicInfo } from "@/design-system/components/item-list/types";
import { css } from "@/domains/emotion";
import { isEmpty } from "lodash-es";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { useMdsHoverContentGroup } from "@/design-system/components/hover-content/MdsHoverContentGroup";
import { MdsHoverContent } from "@/design-system/components/hover-content/MdsHoverContent";
import { CollectionPreview } from "@/design-system/components/item-list/rows/item-preview/CollectionPreview";
import styled from "@emotion/styled";
import { mdsPanelWidthsValue } from "@/design-system/foundations/breakpoints";
import { useAppStore } from "@/store";
import { useThemedCollectionColors } from "@/mds/themes/useThemedCollectionColors";

export interface MdsItemListRowFeaturedContentProps {
  collections?: TopicInfo[];
  snippet?: SnippetChunk[];
  itemId?: string;
  showAll?: boolean;
  isLoading?: boolean;
  inline?: boolean;
  compact?: boolean;
}

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const MdsItemListRowFeaturedContent: FC<MdsItemListRowFeaturedContentProps> = observer(
  ({ collections, snippet, itemId, showAll, isLoading, inline, compact = false }) => {
    const { store } = useAppStore();
    const length = showAll ? collections?.length : 2;
    const anyCollections = !isEmpty(collections);
    const firstTwoCollections = collections?.slice(0, length);
    const remainingCollections = collections?.slice(length);
    const additionalCollectionsCount = remainingCollections?.length;
    const shouldShowAdditionalCollections = !!additionalCollectionsCount;
    const group = useMdsHoverContentGroup();

    if (isLoading) {
      return <LoaderContainer></LoaderContainer>;
    }
    if (!collections?.length && !snippet?.length) return null;

    return (
      <Container
        $wrap={showAll ?? false}
        $inline={inline}
        $flex={inline || (collections && !snippet)}
        $compact={compact}
      >
        {firstTwoCollections?.map(featuredCollection => {
          return (
            <MdsHoverContent
              key={featuredCollection.id}
              group={group}
              content={() => {
                return itemId ? (
                  <CollectionPreview
                    collectionTopic={featuredCollection}
                    noteId={itemId}
                    onDismiss={() => {
                      group.hideAll({});
                    }}
                  />
                ) : null;
              }}
            >
              <CollectionChip
                collectionId={featuredCollection.id}
                label={featuredCollection.label}
                $inlineBlock={!inline}
                onClick={() => {
                  store.navigation.goToCollection({ collectionId: featuredCollection.id });
                }}
              />
            </MdsHoverContent>
          );
        })}

        {shouldShowAdditionalCollections && (
          <CollectionOverflow>+{additionalCollectionsCount}</CollectionOverflow>
        )}
        {anyCollections && snippet && snippet.length > 0 && (
          <DotSeparatorIcon
            innerStyles={{ Icon: { className: dotSeparatorInnerStyles } }}
            kind={MdsIconKind.Dot}
          />
        )}
        {snippet?.map(({ text, isHighlight, isTruncatedStart, isTruncatedEnd }, index) => (
          <Fragment key={index}>
            {isTruncatedStart && "…"}
            {isHighlight ? <HighlightedSnippet>{text}</HighlightedSnippet> : text}
            {isTruncatedEnd && "…"}
          </Fragment>
        ))}
      </Container>
    );
  }
);

interface CollectionChipProps {
  collectionId: string;
  label: string;
  $inlineBlock?: boolean;
  onClick?: () => void;
}
const CollectionChip = ({ collectionId, label, $inlineBlock, onClick }: CollectionChipProps) => {
  const { bgColor, textColor } = useThemedCollectionColors(collectionId);
  return (
    <CollectionChipContainer $inlineBlock={$inlineBlock} onClick={onClick}>
      <CollectionChipWrapper
        $inlineBlock={$inlineBlock}
        style={{ backgroundColor: bgColor, color: textColor }}
      >
        {label}
      </CollectionChipWrapper>
    </CollectionChipContainer>
  );
};

// enforce 16px height so when rendered in the List there's not noticeable layout shift
const CollectionChipContainer = styled.button<{ $inlineBlock?: boolean }>`
  ${({ $inlineBlock }) => $inlineBlock && `display: inline-block;`}
  height: 16px;
  padding: 0;
`;

const CollectionChipWrapper = styled.div<{ $inlineBlock?: boolean }>`
  ${({ $inlineBlock }) => $inlineBlock && `display: inline-block;`}
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  padding-left: 4px;
  padding-right: 3px;
  border-radius: 4px;
  margin-right: 4px;
`;

const DotSeparatorIcon = styled(MdsIcon)`
  height: 100%;
  margin-right: 2px;
`;

const dotSeparatorInnerStyles = css`
  font-size: 4px;
`;

const HighlightedSnippet = styled.span`
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
`;

interface ContainerProps {
  $wrap: boolean;
  $inline?: boolean;
  $flex?: boolean;
  $compact?: boolean;
}

const Container = styled("div")<ContainerProps>`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes.small};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: ${({ $compact, $inline, theme }) => (!$compact && $inline ? theme.spacing.xs : 0)};

  @container mds-row-item (max-width: ${mdsPanelWidthsValue.narrow}px) {
    margin-left: 0;
  }

  ${({ $compact, $flex }) =>
    $flex &&
    `
      display: flex;
      flex-wrap: nowrap;
      ${!$compact && "flex: 1 0;"}
    `}
`;

const CollectionOverflow = styled.span`
  display: inline-block;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  padding-left: 4px;
  padding-right: 3px;
  border-radius: 4px;
  margin-right: 7px;
  color: ${({ theme }) => theme.colors.text.secondary};
  background-color: ${({ theme }) => theme.colors.background.badge.default};
`;

const LoaderContainer = styled.div({
  height: "16px",
});
